import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { SUPPORTED } from '../i18n';

/**
 * useLang — reads the language from the URL pathname (/fr/... or /en/...),
 * syncs i18n, persists choice to localStorage, and exposes helpers.
 */
export const useLang = () => {
  const location = useLocation();
  const { i18n } = useTranslation();

  const firstSegment = location.pathname.split('/').filter(Boolean)[0];
  const lang = SUPPORTED.includes(firstSegment) ? firstSegment : 'fr';

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    try { localStorage.setItem('smarch_lang', lang); } catch { /* ignore */ }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang, i18n]);

  // Map of route segments for FR ↔ EN
  // Note: keys are FR canonical names; both langs use the same path structure
  // We use distinct segments per language for SEO.
  const routes = lang === 'en'
    ? {
        home: '/en',
        projects: '/en/projects',
        project: (slug) => `/en/projects/${slug}`,
        agency: '/en/agency',
        careers: '/en/careers',
        news: '/en/news',
        contact: '/en/contact',
      }
    : {
        home: '/fr',
        projects: '/fr/projets',
        project: (slug) => `/fr/projets/${slug}`,
        agency: '/fr/agence',
        careers: '/fr/carrieres',
        news: '/fr/actualites',
        contact: '/fr/contact',
      };

  /**
   * Build the equivalent path in the OTHER language for the current URL.
   * Used by the FR/EN switcher in the Header.
   */
  const otherLang = lang === 'fr' ? 'en' : 'fr';

  const switchLangPath = () => {
    const path = location.pathname;
    // Map FR routes <-> EN routes
    const map = [
      [/^\/fr\/projets\/(.+)$/, (m) => `/en/projects/${m[1]}`],
      [/^\/fr\/projets$/, () => '/en/projects'],
      [/^\/fr\/agence$/, () => '/en/agency'],
      [/^\/fr\/carrieres$/, () => '/en/careers'],
      [/^\/fr\/actualites$/, () => '/en/news'],
      [/^\/fr\/contact$/, () => '/en/contact'],
      [/^\/fr\/?$/, () => '/en'],
      [/^\/en\/projects\/(.+)$/, (m) => `/fr/projets/${m[1]}`],
      [/^\/en\/projects$/, () => '/fr/projets'],
      [/^\/en\/agency$/, () => '/fr/agence'],
      [/^\/en\/careers$/, () => '/fr/carrieres'],
      [/^\/en\/news$/, () => '/fr/actualites'],
      [/^\/en\/contact$/, () => '/fr/contact'],
      [/^\/en\/?$/, () => '/fr'],
    ];
    for (const [re, to] of map) {
      const m = path.match(re);
      if (m) return to(m);
    }
    return `/${otherLang}`;
  };

  return { lang, otherLang, routes, switchLangPath };
};

export default useLang;
